import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { authService } from './auth-service.js';
const AuthContext = createContext(undefined);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [isLoadingUserData, setIsLoadingUserData] = useState(false);
    const loadUserData = async (userId, isFromNavigation = false) => {
        // Prevent concurrent role loading operations to avoid navigation conflicts
        if (isLoadingUserData) {
            console.log('🔄 User data already loading, skipping duplicate request');
            return;
        }
        try {
            setIsLoadingUserData(true);
            console.log(`🔍 Loading user role for user: ${userId} ${isFromNavigation ? '(navigation)' : ''}`);
            // Resilient getUserRole with navigation-friendly timeout handling
            const attemptGetUserRole = async (timeoutMs, attempt) => {
                console.log(`🔄 Attempt ${attempt}: getUserRole with ${timeoutMs}ms timeout`);
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error(`getUserRole timeout after ${timeoutMs}ms (attempt ${attempt})`)), timeoutMs);
                });
                return Promise.race([
                    authService.getUserRole(),
                    timeoutPromise
                ]);
            };
            // Navigation-friendly timeout strategy: longer initial timeout, more graceful fallback
            let role;
            const maxAttempts = isFromNavigation ? 2 : 3; // Fewer retries for navigation scenarios
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    // Increased timeouts: 8s, 6s, 4s (better for navigation scenarios)
                    const timeout = attempt === 1 ? 8000 : attempt === 2 ? 6000 : 4000;
                    role = await attemptGetUserRole(timeout, attempt);
                    console.log(`✅ User role loaded on attempt ${attempt}:`, role);
                    setUserRole(role);
                    return;
                }
                catch (attemptError) {
                    console.warn(`⚠️ Attempt ${attempt} failed:`, attemptError);
                    if (attempt === maxAttempts) {
                        // More graceful final fallback - don't throw error
                        console.warn('⚠️ All attempts failed, but continuing with cached session data');
                        break;
                    }
                    // Progressive backoff: 1s, 2s between attempts
                    await new Promise(resolve => setTimeout(resolve, attempt * 1000));
                }
            }
        }
        catch (error) {
            console.error('❌ All getUserRole attempts failed:', error);
            console.warn('⚠️ Proceeding without role - user can still access app with limited functionality');
        }
        finally {
            // Always ensure role is set (even if null) to prevent infinite loading states
            if (userRole === undefined) {
                setUserRole(null);
            }
            setIsLoadingUserData(false);
        }
    };
    useEffect(() => {
        // Get initial session
        const initializeAuth = async () => {
            try {
                console.log('🔍 Initializing auth...');
                const { session, error } = await authService.getCurrentSession();
                if (error) {
                    console.error('❌ Error getting current session:', error);
                    setSession(null);
                    setUser(null);
                    setUserRole(null);
                }
                else if (session) {
                    console.log('✅ Found existing session, loading user:', !!session.user);
                    setSession(session);
                    setUser(session.user);
                    if (session.user) {
                        await loadUserData(session.user.id);
                    }
                }
                else {
                    console.log('ℹ️ No existing session found');
                    setSession(null);
                    setUser(null);
                    setUserRole(null);
                }
            }
            catch (error) {
                console.error('❌ Error initializing auth:', error);
                setSession(null);
                setUser(null);
                setUserRole(null);
            }
            finally {
                setLoading(false);
            }
        };
        initializeAuth();
        // Listen for auth changes
        const subscription = authService.onAuthStateChange(async (event, session) => {
            console.log('🔄 Auth state change event:', event, 'session:', !!session, 'user:', !!session?.user);
            // Log detailed information about automatic logouts
            if (event === 'SIGNED_OUT') {
                console.log('🔄 SIGNED_OUT event received - checking if this was user-initiated or automatic');
                console.log('🔄 Current isSigningOut flag:', isSigningOut);
                if (!isSigningOut) {
                    console.warn('⚠️ AUTOMATIC LOGOUT DETECTED - User was signed out without clicking logout');
                    console.warn('⚠️ Possible causes: token expired, invalid session, network issues, or Supabase config');
                }
                else {
                    console.log('✅ User-initiated logout detected');
                }
                setUser(null);
                setSession(null);
                setUserRole(null);
                setLoading(false);
                return;
            }
            // Log other auth events and detect potential loops
            if (event === 'TOKEN_REFRESHED') {
                console.log('🔄 Token refreshed successfully');
            }
            else if (event === 'USER_UPDATED') {
                console.log('🔄 User updated');
            }
            else if (event === 'SIGNED_IN') {
                console.log('✅ User signed in');
                // PRODUCTION LOGGING: Track auth event frequency to detect loops
                const now = Date.now();
                const recentEvents = JSON.parse(sessionStorage.getItem('auth-events') || '[]');
                const fiveSecondsAgo = now - 5000;
                // Keep only recent events
                const filteredEvents = recentEvents.filter((timestamp) => timestamp > fiveSecondsAgo);
                filteredEvents.push(now);
                sessionStorage.setItem('auth-events', JSON.stringify(filteredEvents));
                // Warn about potential auth loops
                if (filteredEvents.length > 3) {
                    console.warn(`⚠️ POTENTIAL AUTH LOOP: ${filteredEvents.length} SIGNED_IN events in 5 seconds`);
                    console.warn('⚠️ This may cause provider portal reloading issues');
                }
            }
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                try {
                    // Mark as navigation-triggered if this is from token refresh or user update
                    const isFromNavigation = event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED';
                    await loadUserData(session.user.id, isFromNavigation);
                }
                catch (error) {
                    console.error('❌ Error loading user data on auth change:', error);
                    // Don't automatically sign out on role loading errors
                    // This could cause automatic logout loops
                    setUserRole(null);
                }
            }
            else {
                setUserRole(null);
            }
            setLoading(false);
        });
        // Add visibility change handler to prevent unnecessary reloads
        const handleVisibilityChange = () => {
            const isVisible = !document.hidden;
            const environment = typeof window !== 'undefined' && window.location?.hostname;
            // PRODUCTION LOGGING: Always log visibility changes in production for debugging
            console.log(`👁️ Tab visibility changed: ${isVisible ? 'VISIBLE' : 'HIDDEN'}`, {
                environment,
                timestamp: new Date().toISOString(),
                hasSession: !!session,
                hasUser: !!user
            });
            // Don't trigger data reload on visibility change - let existing auth handling manage it
            // This prevents the provider portal reloading issue
        };
        // Add visibility event listener
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            // Clean up subscriptions and event listeners
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (subscription && subscription.data && subscription.data.subscription) {
                subscription.data.subscription.unsubscribe();
            }
        };
    }, []);
    const signIn = async (email, password) => {
        const credentials = { email, password };
        const { error } = await authService.signIn(credentials);
        return { error };
    };
    const signOut = async () => {
        setIsSigningOut(true);
        setLoading(true);
        const { error } = await authService.signOut();
        // Force clear all auth state immediately to prevent stale state
        setUser(null);
        setSession(null);
        setUserRole(null);
        setLoading(false);
        // Clear signing out flag after a short delay to prevent auto-redirects
        setTimeout(() => {
            setIsSigningOut(false);
        }, 100);
        return { error };
    };
    const value = {
        user,
        session,
        loading,
        signIn,
        signOut,
        userRole,
        isSigningOut,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
